import { AlertCircle, Check, FileCheck2, Loader2, Upload } from 'lucide-react';
import { Card } from '../ui/card';
import { documentYearLabel, USAGE_LABELS } from '../../lib/taxFlow';

export const TaxUploadZone = ({ id, uploading, onFile, isDark, title, subtitle }) => (
  <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-6 mb-6 border-dashed ${isDark ? 'bg-white/[0.02] border-white/15' : 'bg-white border-black/10'}`}>
    <label
      htmlFor={id}
      data-testid={`${id}-dropzone`}
      className="flex flex-col items-center justify-center text-center cursor-pointer py-6"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
    >
      <div className="w-14 h-14 rounded-2xl bg-[#81d8d0]/15 flex items-center justify-center mb-4">
        {uploading ? <Loader2 className="w-7 h-7 text-[#81d8d0] animate-spin" /> : <Upload className="w-7 h-7 text-[#81d8d0]" />}
      </div>
      <div className="font-medium mb-1">{uploading ? 'Analyse OCR en cours…' : title}</div>
      <div className="text-xs opacity-60 mb-4">{subtitle || 'PDF ou image — 10 Mo maximum'}</div>
      <input
        id={id}
        data-testid={`${id}-input`}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-[#81d8d0] text-black hover:bg-[#6bc4bc] transition">
        Choisir un fichier
      </span>
    </label>
  </Card>
);

const formatValue = (k, v) => {
  if (typeof v !== 'number' || k === 'nombre_parts') return v;
  return `${v.toLocaleString('fr-FR')} €`;
};

export const TaxImportItem = ({ imp, isDark }) => {
  const recognized = Boolean(imp.doc_type);
  const fields = imp.extracted || {};
  const cases = imp.cases || {};
  const fieldKeys = Object.keys(fields);
  const caseKeys = Object.keys(cases);
  const isN1 = imp.usage === 'reference_n1';
  return (
    <div
      data-testid={`import-item-${imp.id}`}
      className={`p-4 rounded-xl border ${
        recognized
          ? isDark ? 'bg-[#81d8d0]/8 border-[#81d8d0]/25' : 'bg-[#81d8d0]/12 border-[#81d8d0]/40'
          : isDark ? 'bg-amber-500/5 border-amber-500/25' : 'bg-amber-50 border-amber-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          recognized ? 'bg-[#81d8d0]/20 text-[#81d8d0]' : 'bg-amber-500/20 text-amber-500'
        }`}>
          {recognized ? <FileCheck2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="font-medium break-all">{imp.filename}</span>
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#81d8d0]/20 text-[#81d8d0]" data-testid={`import-years-${imp.id}`}>
              {documentYearLabel(imp)}
            </span>
            {imp.usage && (
              <span className="text-[10px] uppercase tracking-widest opacity-60">{USAGE_LABELS[imp.usage] || imp.usage}</span>
            )}
          </div>
          <div className="text-xs opacity-70 mb-2">{recognized ? imp.doc_label : 'Document non reconnu'}</div>
          {fieldKeys.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
              {fieldKeys.map((k) => {
                const f = fields[k];
                const v = typeof f === 'object' ? f.value : f;
                return (
                  <div key={k} className="flex items-center gap-2">
                    <Check className="w-3 h-3 text-[#81d8d0]" />
                    <span className="opacity-80">{typeof f === 'object' ? f.label : k} :</span>
                    <strong>{formatValue(k, v)}</strong>
                  </div>
                );
              })}
            </div>
          )}
          {caseKeys.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
              {caseKeys.map((c) => (
                <code key={c} className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10' : 'bg-black/5'}`}>
                  {c} · {Number(cases[c].value).toLocaleString('fr-FR')} €
                </code>
              ))}
            </div>
          )}
          {isN1 && (
            <div className="mt-2 text-[11px] opacity-70">
              Référence uniquement : ces montants servent à comparer, ils ne sont jamais recopiés dans ta déclaration de cette année.
            </div>
          )}
          {imp.matched_rules && imp.matched_rules.length > 0 && (
            <div className="mt-2 text-[11px] text-[#81d8d0]">
              ✓ {imp.matched_rules.length} item{imp.matched_rules.length > 1 ? 's' : ''} pré-coché{imp.matched_rules.length > 1 ? 's' : ''} dans ta checklist
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
